import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSurveyLimit, setSurveyLimit } from '../../api/admin.api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import toast from 'react-hot-toast';

/**
 * 진행중 설문 한도 설정 패널
 *
 * - maxActive: 동시에 진행할 수 있는 설문 최대 개수 (비워두면 무제한)
 * - whitelist: 한도 제한을 받지 않는 userId 목록 (쉼표로 구분)
 *
 * @author hjkim
 */
export const SurveyLimitPanel = () => {
  const queryClient = useQueryClient();
  const [maxActive, setMaxActive] = useState('');
  const [whitelist, setWhitelist] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['surveyLimitConfig'],
    queryFn: getSurveyLimit,
  });

  useEffect(() => {
    if (data) {
      setMaxActive(data.maxActive);
      setWhitelist(data.whitelist);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => setSurveyLimit({ maxActive: maxActive.trim(), whitelist: whitelist.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveyLimitConfig'] });
      toast.success('저장했어요.');
    },
    onError: () => toast.error('저장에 실패했어요. 다시 시도해 주세요.'),
  });

  return (
    <Card className="bg-white rounded-lg shadow-sm">
      <div className="p-4 lg:p-6 space-y-4">
        {/* 헤더 */}
        <div>
          <h2 className="text-lg lg:text-xl font-bold text-[#030213]">진행중 설문 한도</h2>
          <p className="text-sm text-[#6B7280] mt-1">
            동시에 진행할 수 있는 설문 수와 한도 적용 예외 사용자를 설정하세요.
          </p>
        </div>

        {isLoading ? (
          <div className="py-4 text-sm text-[#6B7280]">불러오는 중...</div>
        ) : (
          <div className="space-y-4">
            {/* 최대 개수 */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[#030213]" htmlFor="survey-max-active">
                최대 진행 설문 수
              </label>
              <input
                id="survey-max-active"
                type="number"
                min={1}
                className="border rounded-md px-3 py-1.5 text-sm w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
                placeholder="비워두면 무제한"
                value={maxActive}
                onChange={(e) => setMaxActive(e.target.value)}
              />
              <p className="text-xs text-[#9CA3AF]">비워두면 무제한으로 설문을 개설할 수 있어요.</p>
            </div>

            {/* 화이트리스트 */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[#030213]" htmlFor="survey-whitelist">
                예외 사용자 (화이트리스트)
              </label>
              <input
                id="survey-whitelist"
                type="text"
                className="border rounded-md px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
                placeholder="예외 userId를 쉼표로 구분해 입력하세요 (예: 101,202,303)"
                value={whitelist}
                onChange={(e) => setWhitelist(e.target.value)}
              />
              <p className="text-xs text-[#9CA3AF]">한도와 관계없이 설문을 개설할 수 있는 userId예요.</p>
            </div>

            {/* 저장 버튼 */}
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
